import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AssessmentPopup from './AssessmentPopup';
import { 
  FiCheck, 
  FiSend, 
  FiTrash2, 
  FiEye,
  FiLock,
  FiUnlock,
  FiChevronDown,
  FiFileText
} from 'react-icons/fi';
import { LuCalendarClock } from 'react-icons/lu';
import testService from '../../../services/testService';
import { AssessmentEditIcon } from '../AssessmentContext';
import toast from 'react-hot-toast';
import QuestionPreviewModal from './QuestionPreviewModal';
import RoleSelectionSummary from './RoleSelectionSummary';
import { resolveAssessmentRoleNames } from './assessmentRoleUtils';
import { resolveAssignToSelectionNames } from './resolveAssignToSelectionNames';
import GroupsInlineSearchField from './GroupsInlineSearchField';
import { GroupsPickerMemberRow, GroupsPickerPreviewRow } from './GroupsPickerDropdownPanel';
import GroupsPickerDropdownPortal from './GroupsPickerDropdownPortal';
import GroupsMemberPreviewPopover from './GroupsMemberPreviewPopover';
import { getEffectivePendingEvalCount } from '../../../utils/assessmentEval';

const mapCollegeTemplateGroup = (g) => ({
  id: g.id || g.template_name || g.name,
  name: g.name || g.template_name || g.id,
  template_name: g.template_name || g.name,
  student_count: g.student_count ?? 0,
  student_ids: g.student_ids || [],
  students: g.students || [],
});

const buildHrStudentMap = (groups) => {
  const map = {};
  (groups || []).forEach((tpl) => {
    (tpl.students || []).forEach((s) => {
      if (s?.id != null) {
        map[String(s.id)] = s;
      }
    });
  });
  return map;
};

const toRoleIdList = (userRole) => {
  if (Array.isArray(userRole)) return userRole.map(String).filter(Boolean);
  return String(userRole || '').split(',').map((s) => s.trim()).filter(Boolean);
};

const normalizeBatchRowData = (source = {}) => {
  let selectedCandidates = [];
  if (Array.isArray(source.selectedCandidates)) {
    selectedCandidates = source.selectedCandidates.map(String).filter(Boolean);
  } else if (typeof source.candidates === 'string' && source.candidates) {
    selectedCandidates = source.candidates.split(',').map((s) => s.trim()).filter(Boolean);
  } else if (Array.isArray(source.candidates)) {
    selectedCandidates = source.candidates.map(String).filter(Boolean);
  }

  return {
    ...source,
    user_role: toRoleIdList(source.user_role),
    selectedCandidates,
  };
};

const formatTime = (timeStr) => {
  if (!timeStr) return '—';
  try {
    let hours = 0;
    let minutes = 0;
    const cleaned = timeStr.trim().toUpperCase();
    if (cleaned.includes('AM') || cleaned.includes('PM')) {
      const ampmPart = cleaned.includes('PM') ? 'PM' : 'AM';
      const digitPart = cleaned.replace(/[A-Z]/g, '').trim();
      const parts = digitPart.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1] || '0', 10);
      if (ampmPart === 'PM' && hours < 12) hours += 12;
      if (ampmPart === 'AM' && hours === 12) hours = 0;
    } else {
      const parts = cleaned.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1] || '0', 10);
    }
    if (isNaN(hours) || isNaN(minutes)) return timeStr;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    let displayHour = hours % 12;
    displayHour = displayHour ? displayHour : 12;
    const hPad = String(displayHour).padStart(2, '0');
    const mPad = String(minutes).padStart(2, '0');
    return `${hPad}:${mPad} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('/')) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const TimePicker12Hour = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const dropdownPanelRef = useRef(null);
  const [position, setPosition] = useState(null);

  let initialHour = 10;
  let initialMinute = 0;
  let initialAmpm = 'AM';

  if (value) {
    const parts = value.split(':');
    if (parts.length >= 2) {
      let h = parseInt(parts[0], 10);
      initialMinute = parseInt(parts[1], 10);
      if (isNaN(h)) h = 10;
      if (isNaN(initialMinute)) initialMinute = 0;

      initialAmpm = h >= 12 ? 'PM' : 'AM';
      initialHour = h % 12;
      if (initialHour === 0) initialHour = 12;
    }
  }

  const syncPosition = useCallback(() => {
    if (!dropdownRef.current) return null;
    const rect = dropdownRef.current.getBoundingClientRect();
    const width = 160; // w-[160px]
    let left = rect.left;
    const viewportTop = rect.bottom + 4;
    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    if (left < 8) {
      left = 8;
    }
    return { top: viewportTop, left };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const next = syncPosition();
    if (next) setPosition(next);
  }, [isOpen, syncPosition]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const update = () => {
      const next = syncPosition();
      if (next) setPosition(next);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, syncPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inPanel = dropdownPanelRef.current && dropdownPanelRef.current.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (hourRef.current) {
          const selected = hourRef.current.querySelector('.selected-time-item');
          if (selected) {
            selected.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
        if (minuteRef.current) {
          const selected = minuteRef.current.querySelector('.selected-time-item');
          if (selected) {
            selected.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
      }, 30);
    }
  }, [isOpen]);

  const handleValChange = (newH, newM, newAmpm) => {
    let h24 = newH;
    if (newAmpm === 'PM' && h24 < 12) h24 += 12;
    if (newAmpm === 'AM' && h24 === 12) h24 = 0;

    const hStr = String(h24).padStart(2, '0');
    const mStr = String(newM).padStart(2, '0');
    onChange(`${hStr}:${mStr}`);
  };

  const formattedHour = String(initialHour).padStart(2, '0');
  const formattedMinute = String(initialMinute).padStart(2, '0');

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between bg-[#fcfdfe] border rounded-full px-3 py-1.5 cursor-pointer shadow-sm select-none transition-all w-[115px] hover:bg-slate-50 border-slate-200 focus:outline-none ${
          isOpen ? 'border-red-500 ring-2 ring-red-100' : ''
        } ${disabled ? 'opacity-80 cursor-default pointer-events-none' : ''}`}
      >
        <span className="text-xs font-bold text-slate-700 tracking-wide">
          {formattedHour}:{formattedMinute} {initialAmpm}
        </span>
        <svg className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {isOpen && position && createPortal(
        <div 
          ref={dropdownPanelRef}
          className="bg-white border border-slate-200 rounded-2xl shadow-xl p-2.5 flex flex-col gap-2 w-[160px]"
          style={{ 
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 999999
          }}
        >
          <style>{`
            .custom-timepicker-scroll::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          
          {/* Selected Display Row */}
          <div className="grid grid-cols-3 gap-1 pb-2 border-b border-slate-100">
            <div className="h-8 rounded border-2 border-slate-800 bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {formattedHour}
            </div>
            <div className="h-8 rounded bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {formattedMinute}
            </div>
            <div className="h-8 rounded bg-slate-200/80 text-slate-800 font-extrabold flex items-center justify-center text-xs select-none">
              {initialAmpm}
            </div>
          </div>

          {/* Picker Columns */}
          <div className="flex justify-between gap-1 h-[160px]">
            {/* Hour Column */}
            <div 
              ref={hourRef}
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                const hVal = String(h).padStart(2, '0');
                const isSelected = h === initialHour;
                return (
                  <button
                    type="button"
                    key={h}
                    onClick={() => handleValChange(h, initialMinute, initialAmpm)}
                    className={`w-full py-1 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'selected-time-item text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {hVal}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 self-stretch" />

            {/* Minute Column */}
            <div 
              ref={minuteRef}
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {Array.from({ length: 60 }, (_, i) => i).map((m) => {
                const mVal = String(m).padStart(2, '0');
                const isSelected = m === initialMinute;
                return (
                  <button
                    type="button"
                    key={m}
                    onClick={() => handleValChange(initialHour, m, initialAmpm)}
                    className={`w-full py-1 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'selected-time-item text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {mVal}
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px bg-slate-100 self-stretch" />

            {/* AM/PM Column */}
            <div 
              className="custom-timepicker-scroll flex-1 flex flex-col gap-1 overflow-y-auto"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {['AM', 'PM'].map((ampm) => {
                const isSelected = ampm === initialAmpm;
                return (
                  <button
                    type="button"
                    key={ampm}
                    onClick={() => handleValChange(initialHour, initialMinute, ampm)}
                    className={`w-full py-1.5 text-center text-xs font-bold rounded hover:bg-slate-100 transition-colors cursor-pointer border-0 outline-none ${
                      isSelected ? 'text-red-600 bg-red-50/50' : 'text-slate-600'
                    }`}
                  >
                    {ampm}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const CandidateListPreviewPopover = ({
  data,
  getSortedAssignedCandidateIds,
  getHrStudent,
  getCandidateLabel,
  isHrFresher,
  isPublished
}) => {
  const [activeTab, setActiveTab] = useState('All');
  const allIds = getSortedAssignedCandidateIds();
  const statuses = data.candidate_statuses || {};
  
  const completedCount = allIds.filter(id => statuses[String(id)] === 'Completed' || statuses[String(id)] === 'Evaluated').length;
  const inProgressCount = allIds.filter(id => statuses[String(id)] === 'In Progress').length;
  const notStartedCount = allIds.length - completedCount - inProgressCount;
  const pendingTabCount = inProgressCount + notStartedCount;

  const filteredIds = allIds.filter(id => {
    const s = statuses[String(id)];
    if (activeTab === 'Completed') return s === 'Completed' || s === 'Evaluated';
    if (activeTab === 'Pending') return s !== 'Completed' && s !== 'Evaluated';
    return true;
  });

  const getCandidateStatus = (id) => {
    const s = statuses[String(id)];
    if (s === 'Completed' || s === 'Evaluated') return 'Completed';
    if (s === 'In Progress') return 'Pending';
    return 'Not Started';
  };

  const tabs = (
    <div className="flex bg-slate-100 p-1 rounded-lg mt-2 mb-1 mx-3 gap-1">
      <button 
        onClick={() => setActiveTab('All')}
        className={`flex-1 text-[10px] font-bold py-1.5 px-2 rounded-md transition-all text-center ${activeTab === 'All' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`}
      >
        All {allIds.length}
      </button>
      <button 
        onClick={() => setActiveTab('Completed')}
        className={`flex-1 text-[10px] font-bold py-1.5 px-2 rounded-md transition-all text-center ${activeTab === 'Completed' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-emerald-600 hover:bg-slate-200/50'}`}
      >
        Completed {completedCount}
      </button>
      <button 
        onClick={() => setActiveTab('Pending')}
        className={`flex-1 text-[10px] font-bold py-1.5 px-2 rounded-md transition-all text-center ${activeTab === 'Pending' ? 'bg-white text-orange-600 shadow-sm' : 'text-slate-500 hover:text-orange-600 hover:bg-slate-200/50'}`}
      >
        Pending {pendingTabCount}
      </button>
    </div>
  );

  return (
    <GroupsMemberPreviewPopover
      badgeCount={allIds.length}
      subtitle={isHrFresher ? 'External Setup' : 'Candidate List'}
      headerBottom={tabs}
      trigger={
        <div className="flex items-center gap-1.5 font-black uppercase tracking-tighter cursor-help text-slate-600 hover:text-slate-800 transition-colors">
          <i className="bi bi-people-fill text-xs"></i>
          <span>{String(data.candidates).split(',').length} Candidates</span>
        </div>
      }
    >
      {filteredIds.map((id) => {
        const hrStudent = isHrFresher ? getHrStudent(id) : null;
        const label = getCandidateLabel(id);
        const status = getCandidateStatus(id);
        return isHrFresher ? (
          <GroupsPickerPreviewRow
            key={id}
            name={hrStudent?.student_name || `Student #${id}`}
            meta={hrStudent?.register_number || '—'}
            status={status}
          />
        ) : (
          <GroupsPickerPreviewRow 
            key={id} 
            name={label} 
            status={status}
          />
        );
      })}
    </GroupsMemberPreviewPopover>
  );
};


const BatchRow = ({
  batch,
  assessmentId,
  onRefresh,
  onPackDeleted,
  assignedRoles,
  rolesList = [],
  assessmentCategory,
  onPreview,
  templates = [],
  assessment,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [collegeTemplates, setCollegeTemplates] = useState([]);
  const [hrStudentById, setHrStudentById] = useState({});
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [data, setData] = useState(() => normalizeBatchRowData(batch));
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });

  useEffect(() => {
    if (!isEditing) {
      setData(normalizeBatchRowData(batch));
    }
  }, [batch, isEditing]);

  const updateLocalTestGroup = (testId, groupId, updatedFields) => {
    const isMatchingGroup = (g) =>
      String(g.id) === String(groupId) ||
      (g.backendPackId && String(g.backendPackId) === String(groupId)) ||
      (g.backendId && String(g.backendId) === String(groupId));

    // 1. Check published_tests
    const pubStr = localStorage.getItem('published_tests');
    if (pubStr) {
      try {
        const parsed = JSON.parse(pubStr);
        const testIndex = parsed.findIndex(t => 'pub-' + t.id === testId || String(t.id) === testId || String(t.backendId) === String(testId) || String(t.id) === String(testId).replace(/^pub-/, ''));
        if (testIndex > -1) {
          const updatedGroups = parsed[testIndex].groups.map(g => {
            if (isMatchingGroup(g)) {
              return { ...g, ...updatedFields };
            }
            return g;
          });
          parsed[testIndex].groups = updatedGroups;
          localStorage.setItem('published_tests', JSON.stringify(parsed));
          window.dispatchEvent(new Event('draft-updated'));
          return true;
        }
      } catch (e) {
        console.error(e);
      }
    }

    // 2. Check draft_assessment
    const draftStr = localStorage.getItem('draft_assessment');
    if (draftStr) {
      try {
        const parsed = JSON.parse(draftStr);
        if (parsed && (String(parsed.id) === testId || 'draft-assessment' === testId || String(parsed.backendId) === String(testId))) {
          const updatedGroups = (parsed.groups || []).map(g => {
            if (isMatchingGroup(g)) {
              return { ...g, ...updatedFields };
            }
            return g;
          });
          parsed.groups = updatedGroups;
          localStorage.setItem('draft_assessment', JSON.stringify(parsed));
          window.dispatchEvent(new Event('draft-updated'));
          return true;
        }
      } catch (e) {
        console.error(e);
      }
    }

    // 3. Check saved_drafts
    const savedStr = localStorage.getItem('saved_drafts');
    if (savedStr) {
      try {
        const parsed = JSON.parse(savedStr);
        const testIndex = parsed.findIndex(t => String(t.id) === testId || String(t.backendId) === String(testId));
        if (testIndex > -1) {
          const updatedGroups = parsed[testIndex].groups.map(g => {
            if (isMatchingGroup(g)) {
              return { ...g, ...updatedFields };
            }
            return g;
          });
          parsed[testIndex].groups = updatedGroups;
          localStorage.setItem('saved_drafts', JSON.stringify(parsed));
          window.dispatchEvent(new Event('draft-updated'));
          return true;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return false;
  };

  const dropdownRef = useRef(null);
  const employeeDropdownPanelRef = useRef(null);
  const isHrFresher = assessment?.category_id 
    ? [2, 4].includes(Number(assessment.category_id))
    : (assessmentCategory === 'HR Recruitment-Fresher' || assessmentCategory === 'External' || assessment?.category === 'HR Recruitment-Fresher' || assessment?.category === 'External');

  const [isGroupRolesOpen, setIsGroupRolesOpen] = useState(false);
  const [groupRolesSearchTerm, setGroupRolesSearchTerm] = useState('');
  const [showSelectedRolesOnly, setShowSelectedRolesOnly] = useState(false);
  const [showSelectedCandidatesOnly, setShowSelectedCandidatesOnly] = useState(false);
  const groupRolesDropdownRef = useRef(null);
  const groupRolesPanelRef = useRef(null);

  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Fetch college templates list once if it is External
  useEffect(() => {
    if (isHrFresher) {
      const fetchExternalSetupGroups = async () => {
        try {
          const res = await testService.getHrCollegeTemplates();
          if (res.status === 'success') {
            const groups = (res.college_templates || []).map(mapCollegeTemplateGroup);
            setCollegeTemplates(groups);
            setHrStudentById(buildHrStudentMap(groups));
          }
        } catch (err) {
          console.error('Failed to fetch External Setup groups:', err);
          toast.error('Failed to load External Setup list');
        }
      };
      fetchExternalSetupGroups();
    } else {
      setCollegeTemplates([]);
      setHrStudentById({});
    }
  }, [isHrFresher]);

  // Fetch candidate list dynamically when role selection changes
  useEffect(() => {
    const roleIds = toRoleIdList(data.user_role);

    if (roleIds.length === 0) {
      setFilteredEmployees([]);
      return;
    }

    setLoadingCandidates(true);
    if (!isHrFresher) {
      const fetchEmployees = async () => {
        try {
          const res = await testService.getUsersByRoles(roleIds.join(','));
          const employees = res?.data ?? (Array.isArray(res) ? res : []);
          setFilteredEmployees(employees || []);
        } catch (err) {
          console.error('Failed to fetch filtered employees:', err);
        } finally {
          setLoadingCandidates(false);
        }
      };
      fetchEmployees();
    } else {
      // External: Fetch students for selected college templates in user_role
      Promise.all(
        roleIds.map(roleName => 
          testService.getStudentsByCollege(roleName)
            .then(res => res.students || [])
            .catch(() => [])
        )
      ).then(results => {
        const allStudents = results.flat();
        const uniqueStudents = [];
        const seenIds = new Set();
        allStudents.forEach(s => {
          const uniqueId = String(s.id || s.username || s.email || '');
          if (uniqueId && !seenIds.has(uniqueId)) {
            seenIds.add(uniqueId);
            uniqueStudents.push({
              id: s.id,
              name: s.student_name || s.name || '',
              email: s.email || s.username || '',
              job_role: s.college_name || '',
            });
          }
        });
        setFilteredEmployees(uniqueStudents);
      })
      .catch(e => console.error(e))
      .finally(() => setLoadingCandidates(false));
    }
  }, [data.user_role, isHrFresher]);

  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    let diff = (eH * 60 + eM) - (sH * 60 + sM);
    if (diff < 0) diff += 1440;
    return diff;
  };

  const handleTimeChange = (field, value) => {
    const newData = { ...data, [field]: value };
    if (field === 'start_time') {
      newData.end_time = value;
    }
    const newDuration = calculateDuration(newData.start_time, newData.end_time);
    setData({ ...newData, duration: newDuration });
  };

  const buildUpdatePayload = () => {
    const userRole = Array.isArray(data.user_role)
      ? data.user_role.join(',')
      : (data.user_role ?? '');
    const candidates = Array.isArray(data.candidates)
      ? data.candidates.join(',')
      : (data.candidates ?? (Array.isArray(data.selectedCandidates) ? data.selectedCandidates.join(',') : ''));

    return {
      assessment_id: assessmentId,
      pack_name: data.pack_name,
      template_id: data.template_id || null,
      user_role: userRole,
      duration: data.duration,
      scheduled_date: data.scheduled_date,
      start_time: data.start_time,
      end_time: data.end_time,
      candidates,
      candidates_type: data.candidates_type,
      status: data.status,
      instructions: data.instructions,
      proctored_exam: data.proctored_exam,
      browser_lockdown: data.browser_lockdown,
      show_results: data.show_results,
      allow_backtracking: data.allow_backtracking,
    };
  };

  // Persists a field (e.g. backendId) on the parent assessment entry in published_tests
  const updateLocalTestAssessment = (testId, updatedFields) => {
    const pubStr = localStorage.getItem('published_tests');
    if (pubStr) {
      try {
        const parsed = JSON.parse(pubStr);
        const testIndex = parsed.findIndex(t => 'pub-' + t.id === testId || String(t.id) === testId || String(t.backendId) === String(testId));
        if (testIndex > -1) {
          parsed[testIndex] = { ...parsed[testIndex], ...updatedFields };
          localStorage.setItem('published_tests', JSON.stringify(parsed));
          window.dispatchEvent(new Event('draft-updated'));
          return true;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return false;
  };

  const deleteLocalTestGroup = (testId, groupId) => {
    // 1. Check published_tests
    const pubStr = localStorage.getItem('published_tests');
    if (pubStr) {
      try {
        const parsed = JSON.parse(pubStr);
        const testIndex = parsed.findIndex(t => 'pub-' + t.id === testId || String(t.id) === testId || String(t.backendId) === String(testId));
        if (testIndex > -1) {
          parsed[testIndex].groups = parsed[testIndex].groups.filter(g => String(g.id) !== String(groupId));
          localStorage.setItem('published_tests', JSON.stringify(parsed));
          window.dispatchEvent(new Event('draft-updated'));
          return true;
        }
      } catch (e) {
        console.error(e);
      }
    }

    // 2. Check draft_assessment
    const draftStr = localStorage.getItem('draft_assessment');
    if (draftStr) {
      try {
        const parsed = JSON.parse(draftStr);
        if (parsed && (String(parsed.id) === testId || 'draft-assessment' === testId)) {
          parsed.groups = (parsed.groups || []).filter(g => String(g.id) !== String(groupId));
          localStorage.setItem('draft_assessment', JSON.stringify(parsed));
          window.dispatchEvent(new Event('draft-updated'));
          return true;
        }
      } catch (e) {
        console.error(e);
      }
    }

    // 3. Check saved_drafts
    const savedStr = localStorage.getItem('saved_drafts');
    if (savedStr) {
      try {
        const parsed = JSON.parse(savedStr);
        const testIndex = parsed.findIndex(t => String(t.id) === testId);
        if (testIndex > -1) {
          parsed[testIndex].groups = parsed[testIndex].groups.filter(g => String(g.id) !== String(groupId));
          localStorage.setItem('saved_drafts', JSON.stringify(parsed));
          window.dispatchEvent(new Event('draft-updated'));
          return true;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return false;
  };

  const handleToggleEdit = async () => {
    if (isEditing) {
      if (!data.pack_name?.trim()) return toast.error('Please enter a group name');
      if (!data.template_id) return toast.error('Please select a template for this group');
      
      const roleIds = Array.isArray(data.user_role)
        ? data.user_role.filter(Boolean)
        : String(data.user_role || '').split(',').map(s => s.trim()).filter(Boolean);

      if (roleIds.length === 0) {
        return toast.error(isHrFresher ? 'Please select at least one college template' : 'Please select at least one assigned role');
      }

      const assignedCandidates = Array.isArray(data.selectedCandidates) ? data.selectedCandidates : [];
      if (assignedCandidates.length === 0) {
        return toast.error(isHrFresher ? 'Please assign at least one candidate student' : 'Please assign at least one member');
      }

      try {
        const payload = buildUpdatePayload();
        const matchedTpl = templates.find(t => String(t.id) === String(payload.template_id));
        const updatedFields = {
          ...payload,
          template_name: matchedTpl?.name || matchedTpl?.template_name || '',
          candidates_list: Array.isArray(data.selectedCandidates) ? data.selectedCandidates.map(String) : []
        };

        if (assessment?.isLocal || String(data.id || '').startsWith('group-')) {
          updateLocalTestGroup(assessmentId, data.id, updatedFields);
          toast.success('Group updated successfully');
          setIsEditing(false);
          onRefresh?.();
        } else {
          const res = await testService.updateTestPack(data.id, payload);
          if (res.status === 'success') {
            updateLocalTestGroup(assessmentId, data.id, updatedFields);
            toast.success('Group updated successfully');
            setIsEditing(false);
            onRefresh?.();
          } else {
            toast.error(res.message || 'Failed to update group');
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err?.message || 'Failed to update group');
      }
    } else {
      setIsEditing(true);
      // Ensure candidates is handled as array for selection UI
      let currentCandidates = [];
      if (typeof data.candidates === 'string' && data.candidates) {
        currentCandidates = data.candidates.split(',').map(s => s.trim());
      } else if (Array.isArray(data.candidates)) {
        currentCandidates = data.candidates;
      }
      const packRoles = batch.user_role
        ? String(batch.user_role).split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      setData((prev) => ({
        ...prev,
        selectedCandidates: currentCandidates,
        user_role: packRoles.length > 0 ? packRoles : prev.user_role || [],
      }));
    }
  };

  const getRolesForForm = () => {
    if (isHrFresher) {
      return collegeTemplates.map(t => ({
        id: t.id,
        name: t.name || t.template_name || t.id
      }));
    }
    return rolesList.map(r => ({ ...r, name: r.name || r.role_name || r.job_role || r.role || r.id }));
  };

  const resolveRoleNames = (roleIds) => resolveAssessmentRoleNames(roleIds, getRolesForForm());

  const selectedRoleIds = toRoleIdList(data.user_role);

  const visibleGroupRoles = getRolesForForm().filter((role) => {
    const matchesSearch = role.name.toLowerCase().includes(groupRolesSearchTerm.toLowerCase());
    const isSelected = selectedRoleIds.includes(String(role.id));
    return matchesSearch && (!showSelectedRolesOnly || isSelected);
  });

  const toggleGroupRole = (roleId) => {
    setData((prev) => {
      const current = toRoleIdList(prev.user_role);
      const updated = current.includes(String(roleId))
        ? current.filter((id) => id !== String(roleId))
        : [...current, String(roleId)];
      return { ...prev, user_role: updated };
    });
  };

  const selectAllGroupRoles = (checked) => {
    if (checked) {
      const allIds = visibleGroupRoles.map((r) => String(r.id));
      setData((prev) => ({ ...prev, user_role: allIds }));
    } else {
      setData((prev) => ({ ...prev, user_role: [] }));
    }
  };

  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);

  // Handle click outside for roles dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = groupRolesDropdownRef.current?.contains(event.target);
      const inPanel = groupRolesPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsGroupRolesOpen(false);
      }
    };
    if (isGroupRolesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setGroupRolesSearchTerm('');
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGroupRolesOpen]);

  // Handle click outside for employee dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAnchor = dropdownRef.current?.contains(event.target);
      const inPanel = employeeDropdownPanelRef.current?.contains(event.target);
      if (!inAnchor && !inPanel) {
        setIsEmployeeDropdownOpen(false);
      }
    };

    if (isEmployeeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setEmployeeSearchTerm('');
      setTemplateSearchTerm('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEmployeeDropdownOpen]);

  const toggleEmployee = (empId) => {
    setData(prev => {
      const current = Array.isArray(prev.selectedCandidates) ? prev.selectedCandidates : [];
      const updated = current.includes(String(empId))
        ? current.filter(id => id !== String(empId))
        : [...current, String(empId)];
      return { ...prev, selectedCandidates: updated, candidates: updated.join(',') };
    });
  };

  const toggleCollegeTemplate = (template) => {
    const ids = (template.student_ids || []).map(String);
    const templateIdStr = String(template.id);
    setData(prev => {
      const current = Array.isArray(prev.selectedCandidates) ? prev.selectedCandidates.map(String) : [];
      const allSelected = ids.length > 0 && ids.every(id => current.includes(id));
      const updated = allSelected
        ? current.filter(id => !ids.includes(id))
        : [...new Set([...current, ...ids])];
      const currentRoles = Array.isArray(prev.user_role)
        ? prev.user_role.map(String)
        : String(prev.user_role || '').split(',').map(s => s.trim()).filter(Boolean);
      const updatedRoles = allSelected
        ? currentRoles.filter(r => r !== templateIdStr)
        : [...new Set([...currentRoles, templateIdStr])];
      return { ...prev, selectedCandidates: updated, candidates: updated.join(','), user_role: updatedRoles };
    });
  };

  const isCollegeTemplateSelected = (template) => {
    const ids = (template.student_ids || []).map(String);
    const current = Array.isArray(data.selectedCandidates) ? data.selectedCandidates.map(String) : [];
    return ids.length > 0 && ids.every(id => current.includes(id));
  };

  const getVisibleCollegeTemplates = () => collegeTemplates.filter(t =>
    (t.name || '').toLowerCase().includes(templateSearchTerm.toLowerCase())
  );

  const getVisibleCollegeTemplatesForAssign = () => collegeTemplates.filter(t =>
    (t.name || '').toLowerCase().includes(employeeSearchTerm.toLowerCase()) &&
    (!showSelectedCandidatesOnly || isCollegeTemplateSelected(t))
  );

  const getVisibleEmployees = () =>
    filteredEmployees.filter(
      (emp) =>
        (
          emp.name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
          (emp.email && emp.email.toLowerCase().includes(employeeSearchTerm.toLowerCase())) ||
          (emp.job_role && emp.job_role.toLowerCase().includes(employeeSearchTerm.toLowerCase()))
        ) &&
        (!showSelectedCandidatesOnly || (Array.isArray(data.selectedCandidates) && data.selectedCandidates.map(String).includes(String(emp.id))))
    );

  const getHrStudent = (id) => hrStudentById[String(id).trim()] || null;

  const getCandidateLabel = (id) => {
    if (isHrFresher) {
      const student = getHrStudent(id);
      return student?.student_name || `Student ID: ${id}`;
    }
    const emp = filteredEmployees.find(e => String(e.id) === String(id));
    return emp ? emp.name : `ID: ${id}`;
  };

  const getAssignedCandidateIds = () =>
    String(data.candidates || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  const getSortedAssignedCandidateIds = () => {
    const ids = getAssignedCandidateIds();
    if (!isHrFresher) return ids;
    return [...ids].sort((a, b) => {
      const na = getHrStudent(a)?.student_name || '';
      const nb = getHrStudent(b)?.student_name || '';
      return na.localeCompare(nb, undefined, { sensitivity: 'base' });
    });
  };

  const handleDelete = () => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this group?',
      message: 'Are you sure you want to delete this group? This action cannot be undone.',
      confirmText: 'Yes, delete it!',
      onConfirm: async () => {
        try {
          if (assessment?.isLocal || String(data.id || '').startsWith('group-')) {
            deleteLocalTestGroup(assessmentId, data.id);
            setPopupConfig({ isOpen: false });
            toast.success('Group deleted successfully');
            if (assessmentId && data.id) {
              onPackDeleted?.(assessmentId, data.id);
            } else {
              onRefresh?.();
            }
          } else {
            const res = await testService.deleteTestPack(data.id);
            setPopupConfig({ isOpen: false });
            if (res.status === 'success') {
              toast.success('Group deleted successfully');
              if (assessmentId && data.id) {
                onPackDeleted?.(assessmentId, data.id);
              } else {
                onRefresh?.();
              }
            } else {
              toast.error(res.message || 'Failed to delete group');
            }
          }
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
          toast.error('Failed to delete group');
        }
      }
    });
  };

  const isPublished = data.status?.toLowerCase() === 'published';
  const pendingEvalCount = getEffectivePendingEvalCount(data);

  return (
    <tr className={`group transition-all ${isEditing ? 'bg-blue-50/50' : 'hover:bg-slate-50/30'} ${isPublished && !isEditing ? 'published-row' : ''}`}>
      <td className="px-6 py-5 assess-mod-groups-col-name align-middle">
        {isEditing && !isPublished ? (
          <input 
            type="text" 
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-rose-100 outline-none transition-all"
            value={data.pack_name}
            onChange={(e) => setData({...data, pack_name: e.target.value})}
          />
        ) : (
          <div className="flex flex-col gap-1.5">
            <div className={`assess-mod-groups-pack-name uppercase tracking-wide ${isPublished ? 'text-[#059669]' : 'text-slate-700'}`}>{data.pack_name}</div>
            <div className={`assess-mod-groups-status inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold uppercase tracking-wide w-fit shadow-sm ${isPublished ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-slate-50 text-slate-500 border border-slate-100'}`}>
              <div className={`w-1 h-1 rounded-full ${isPublished ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
              {isPublished ? 'Published' : 'Draft'}
            </div>
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-template align-middle">
        {isEditing && !isPublished ? (
          <select 
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-rose-100 outline-none transition-all"
            value={data.template_id}
            onChange={(e) => setData({...data, template_id: e.target.value})}
          >
             <option value="">Select Template</option>
             {templates.map(t => (
               <option key={t.id} value={t.id}>{t.name}</option>
             ))}
          </select>
        ) : (
          <span className={`font-black text-xs uppercase tracking-wide ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {data.template_name || 'No Template Assigned'}
          </span>
        )}
      </td>
      {!isHrFresher && (
        <td className="px-6 py-5 assess-mod-groups-col-roles">
          {(() => {
            const selectedRoleNames = resolveRoleNames(data.user_role);
            const selectedRoleNamesText = selectedRoleNames.join(', ');
            return isEditing && !isPublished ? (
            <div className="assess-mod-groups-picker-wrap" ref={groupRolesDropdownRef}>
              <GroupsInlineSearchField
                value={groupRolesSearchTerm}
                onChange={(e) => {
                  setGroupRolesSearchTerm(e.target.value);
                  setShowSelectedRolesOnly(false);
                  setIsGroupRolesOpen(true);
                }}
                onFocus={() => {
                  setShowSelectedRolesOnly(false);
                  setIsGroupRolesOpen(true);
                }}
                onBlur={() => {
                  if (!groupRolesSearchTerm) return;
                  window.setTimeout(() => {
                    if (!groupRolesDropdownRef.current?.contains(document.activeElement)) {
                      setGroupRolesSearchTerm('');
                    }
                  }, 150);
                }}
                placeholder={isHrFresher ? 'Search colleges...' : 'Search roles...'}
                dropdownOpen={isGroupRolesOpen}
                onToggleDropdown={() => {
                  setShowSelectedRolesOnly(false);
                  setIsGroupRolesOpen((open) => {
                    const next = !open;
                    if (!next) setGroupRolesSearchTerm('');
                    return next;
                  });
                }}
                selectionSummary={
                  selectedRoleIds.length > 0 ? (
                    <RoleSelectionSummary
                      names={selectedRoleNames}
                      totalCount={getRolesForForm().length}
                      summaryMode="count"
                    />
                  ) : null
                }
                selectionSummaryTitle={selectedRoleNamesText}
                toggleAriaLabel="Toggle roles list"
                showViewSelected={selectedRoleNames.length > 0}
                onViewSelected={() => {
                  setGroupRolesSearchTerm('');
                  setShowSelectedRolesOnly(true);
                  setIsGroupRolesOpen(true);
                }}
                viewSelectedTooltip={`View selected roles: ${selectedRoleNamesText}`}
              />
              <GroupsPickerDropdownPortal
                ref={groupRolesPanelRef}
                isOpen={isGroupRolesOpen}
                anchorRef={groupRolesDropdownRef}
                minWidth={288}
                title={isHrFresher ? 'Assigned Colleges' : 'Assigned Roles'}
                subtitle={showSelectedRolesOnly ? 'Selected Role List' : (isHrFresher ? 'College List' : 'Role List')}
                count={selectedRoleIds.length}
              >
                  <div className="assess-mod-groups-member-panel__list">
                    {!showSelectedRolesOnly && (
                      <GroupsPickerMemberRow
                        isSelectAll
                        selectAllLabel="Select All"
                        checked={
                          visibleGroupRoles.length > 0 &&
                          visibleGroupRoles.every((r) =>
                            selectedRoleIds.includes(String(r.id))
                          )
                        }
                        onChange={(e) => selectAllGroupRoles(e.target.checked)}
                      />
                    )}
                    {visibleGroupRoles.map((role) => (
                      <GroupsPickerMemberRow
                        key={role.id}
                        name={role.name}
                        checked={selectedRoleIds.includes(String(role.id))}
                        onChange={() => toggleGroupRole(role.id)}
                      />
                    ))}
                  </div>
                  {visibleGroupRoles.length === 0 && (
                    <p className="assess-mod-groups-member-panel__empty">
                      {showSelectedRolesOnly ? 'No selected roles' : 'No roles found'}
                    </p>
                  )}
              </GroupsPickerDropdownPortal>
            </div>
          ) : (
            <div className="assess-mod-groups-meta font-semibold text-slate-600">
              {selectedRoleNames.length > 0 ? (
                <GroupsMemberPreviewPopover
                  title="Assigned Roles"
                  subtitle="Role List"
                  badgeCount={selectedRoleNames.length}
                  trigger={
                    <div className={`flex items-center gap-1.5 font-bold hover:text-slate-900 transition-colors cursor-help group select-none ${isPublished ? 'text-[#059669]' : 'text-slate-700'}`}>
                      <span className="max-w-[200px] truncate block font-black text-xs uppercase tracking-wide">
                        <RoleSelectionSummary
                          names={selectedRoleNames}
                          totalCount={getRolesForForm().length}
                        />
                      </span>
                    </div>
                  }
                >
                  {selectedRoleNames.map((role, idx) => (
                    <GroupsPickerPreviewRow
                      key={idx}
                      name={role}
                    />
                  ))}
                </GroupsMemberPreviewPopover>
              ) : (
                <span className="text-slate-300">No Roles Assigned</span>
              )}
            </div>
          );
          })()}
        </td>
      )}
      <td className="px-6 py-5 assess-mod-groups-col-assign align-middle">
        {isEditing && !isPublished ? (
          <div className="assess-mod-groups-picker-wrap" ref={dropdownRef}>
            {(() => {
              const selectedCandidateNames = resolveAssignToSelectionNames({
                isHr: isHrFresher,
                selectedIds: data.selectedCandidates,
                collegeTemplates,
                employees: filteredEmployees,
              });
              const selectedCandidateNamesText = selectedCandidateNames.join(', ');
              return (
                <>
            <GroupsInlineSearchField
              value={employeeSearchTerm}
              onChange={(e) => {
                setEmployeeSearchTerm(e.target.value);
                setShowSelectedCandidatesOnly(false);
                setIsEmployeeDropdownOpen(true);
              }}
              onFocus={() => {
                setShowSelectedCandidatesOnly(false);
                setIsEmployeeDropdownOpen(true);
              }}
              placeholder={
                isHrFresher
                  ? 'Search candidates...'
                  : (selectedRoleIds.length === 0
                      ? 'Select roles first'
                      : 'Search candidates...')
              }
              disabled={!isHrFresher && selectedRoleIds.length === 0}
              dropdownOpen={isEmployeeDropdownOpen}
              onToggleDropdown={() => {
                if (!isHrFresher && selectedRoleIds.length === 0) {
                  toast.error('Select assigned roles first');
                  return;
                }
                setShowSelectedCandidatesOnly(false);
                setIsEmployeeDropdownOpen((open) => !open);
              }}
              selectionSummary={selectedCandidateNames.length > 0 ? (
                <RoleSelectionSummary
                  names={selectedCandidateNames}
                  summaryMode="count"
                  itemLabel={isHrFresher ? 'groups' : 'candidates'}
                />
              ) : null}
              selectionSummaryTitle={selectedCandidateNamesText}
              toggleAriaLabel="Toggle candidates list"
              showViewSelected={selectedCandidateNames.length > 0}
              onViewSelected={() => {
                setEmployeeSearchTerm('');
                setShowSelectedCandidatesOnly(true);
                setIsEmployeeDropdownOpen(true);
              }}
              viewSelectedTooltip={`View selected ${isHrFresher ? 'groups' : 'candidates'}: ${selectedCandidateNamesText}`}
            />

            <GroupsPickerDropdownPortal
              ref={employeeDropdownPanelRef}
              isOpen={isEmployeeDropdownOpen}
              anchorRef={dropdownRef}
              minWidth={256}
              title={isHrFresher ? "External Setup Groups" : "Group Members"}
              subtitle={
                showSelectedCandidatesOnly
                  ? (isHrFresher ? "Selected Group List" : "Selected Candidate List")
                  : (isHrFresher ? "College Template Groups" : "Candidate List")
              }
              count={Array.isArray(data.selectedCandidates) ? data.selectedCandidates.length : 0}
            >
                <div className="assess-mod-groups-member-panel__list">
                  {isHrFresher ? (
                    <>
                      {!showSelectedCandidatesOnly && (
                        <GroupsPickerMemberRow
                          isSelectAll
                          selectAllLabel="Select All Groups"
                          checked={(() => {
                            const visible = getVisibleCollegeTemplatesForAssign();
                            return (
                              visible.length > 0 &&
                              visible.every((t) => isCollegeTemplateSelected(t))
                            );
                          })()}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const visible = getVisibleCollegeTemplatesForAssign();
                            const allStudentIds = visible.flatMap(t => (t.student_ids || []).map(String));
                            const visibleTemplateIds = visible.map(t => String(t.id));
                            setData((prev) => {
                              const current = Array.isArray(prev.selectedCandidates)
                                ? prev.selectedCandidates.map(String)
                                : [];
                              const updated = checked
                                ? [...new Set([...current, ...allStudentIds])]
                                : current.filter((id) => !allStudentIds.includes(id));
                              const currentRoles = Array.isArray(prev.user_role)
                                ? prev.user_role.map(String)
                                : String(prev.user_role || '').split(',').map(s => s.trim()).filter(Boolean);
                              const updatedRoles = checked
                                ? [...new Set([...currentRoles, ...visibleTemplateIds])]
                                : currentRoles.filter(r => !visibleTemplateIds.includes(r));
                              return { ...prev, selectedCandidates: updated, candidates: updated.join(','), user_role: updatedRoles };
                            });
                          }}
                        />
                      )}
                      {getVisibleCollegeTemplatesForAssign().map(template => (
                        <GroupsPickerMemberRow
                          key={template.id}
                          name={template.name}
                          meta={`${(template.student_ids || []).length} Students`}
                          checked={isCollegeTemplateSelected(template)}
                          onChange={() => toggleCollegeTemplate(template)}
                        />
                      ))}
                    </>
                  ) : (
                    <>
                      {!showSelectedCandidatesOnly && (
                        <GroupsPickerMemberRow
                          isSelectAll
                          selectAllLabel="Select All Members"
                          checked={(() => {
                            const visible = getVisibleEmployees();
                            return (
                              visible.length > 0 &&
                              visible.every((emp) =>
                                Array.isArray(data.selectedCandidates) &&
                                data.selectedCandidates.map(String).includes(String(emp.id))
                              )
                            );
                          })()}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const visible = getVisibleEmployees();
                            const visibleIds = visible.map((emp) => String(emp.id));
                            setData((prev) => {
                              const current = Array.isArray(prev.selectedCandidates)
                                ? prev.selectedCandidates.map(String)
                                : [];
                              const updated = checked
                                ? [...new Set([...current, ...visibleIds])]
                                : current.filter((id) => !visibleIds.includes(id));
                              return { ...prev, selectedCandidates: updated, candidates: updated.join(',') };
                            });
                          }}
                        />
                      )}
                      {getVisibleEmployees().map(emp => (
                        <GroupsPickerMemberRow
                          key={emp.id}
                          name={emp.name}
                          meta={emp.email || emp.job_role}
                          checked={Array.isArray(data.selectedCandidates) && data.selectedCandidates.map(String).includes(String(emp.id))}
                          onChange={() => toggleEmployee(emp.id)}
                        />
                      ))}
                    </>
                  )}
                </div>
                {((isHrFresher ? getVisibleCollegeTemplatesForAssign() : getVisibleEmployees()).length === 0) && (
                  <p className="assess-mod-groups-member-panel__empty">
                    {showSelectedCandidatesOnly
                      ? (isHrFresher ? "No selected groups" : "No selected candidates")
                      : (isHrFresher ? "No college templates found" : "No candidates found")}
                  </p>
                )}
            </GroupsPickerDropdownPortal>
                </>
              );
            })()}
          </div>
        ) : (
          <div className="assess-mod-groups-meta font-semibold text-slate-600 flex flex-col gap-0.5">
            {data.candidates ? (
              <>
                <CandidateListPreviewPopover
                  data={data}
                  getSortedAssignedCandidateIds={getSortedAssignedCandidateIds}
                  getHrStudent={getHrStudent}
                  getCandidateLabel={getCandidateLabel}
                  isHrFresher={isHrFresher}
                  isPublished={isPublished}
                />
              </>
            ) : (
              <span className="text-slate-300">No Assignment</span>
            )}
            <div className="mt-1 flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
              <div className="flex items-center gap-1 text-emerald-600">
                <i className="bi bi-check-all text-xs"></i>
                <span className="font-black uppercase tracking-widest text-xs leading-none whitespace-nowrap">{data.completed_count || 0} COMPLETED</span>
              </div>
              {pendingEvalCount > 0 && (
                <div className="flex items-center gap-1 text-red-500">
                  <i className="bi bi-clock-history text-xs"></i>
                  <span className="font-black uppercase tracking-widest text-xs leading-none whitespace-nowrap">{pendingEvalCount} PENDING EVAL</span>
                </div>
              )}
            </div>
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-date align-middle">
        {isEditing ? (
          <div className="relative flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm hover:border-slate-300 transition-all w-full max-w-[150px] min-w-[130px] h-[36px]">
            <span className="text-xs font-bold text-slate-700 tracking-wide select-none">
              {formatDateToDDMMYYYY(data.scheduled_date) || 'Select Date'}
            </span>
            <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <input
              type="date"
              className="assess-mod-groups-datetime-input absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              value={data.scheduled_date}
              onChange={(e) => setData({ ...data, scheduled_date: e.target.value })}
            />
          </div>
        ) : (
          <div className={`font-black text-xs tracking-wider whitespace-nowrap ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {formatDateToDDMMYYYY(data.scheduled_date) || '—'}
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-start align-middle">
        {isEditing ? (
          <TimePicker12Hour
            disabled={false}
            value={data.start_time}
            onChange={(val) => handleTimeChange('start_time', val)}
          />
        ) : (
          <div className={`font-black text-xs tracking-wider uppercase whitespace-nowrap ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {formatTime(data.start_time) || '—'}
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-end align-middle">
        {isEditing ? (
          <TimePicker12Hour
            disabled={false}
            value={data.end_time}
            onChange={(val) => handleTimeChange('end_time', val)}
          />
        ) : (
          <div className={`font-black text-xs tracking-wider uppercase whitespace-nowrap ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {formatTime(data.end_time) || '—'}
          </div>
        )}
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-duration align-middle">
        <div className="assess-mod-groups-duration-wrap">
          <span className={`font-black text-sm whitespace-nowrap ${isPublished ? 'text-[#059669]' : 'text-slate-600'}`}>
            {data.duration ?? '—'}
          </span>
          <span className="text-slate-400 font-black text-xs uppercase">M</span>
        </div>
      </td>
      <td className="px-6 py-5 assess-mod-groups-col-action align-middle text-right">
        <div className="assess-mod-groups-action-wrap">
          {isPublished ? (
            <>
              <button 
                onClick={() => setIsPreviewOpen(true)}
                className="assess-mod-groups-action-btn bg-slate-50 text-slate-500 hover:bg-slate-500 hover:text-white border border-slate-100 group"
                title="Preview Question Paper"
              >
                <FiEye className="group-hover:scale-110 transition-transform" />
              </button>

              <button 
                onClick={handleToggleEdit}
                className={`assess-mod-groups-action-btn group ${isEditing ? 'bg-emerald-600 text-white' : 'bg-orange-50 text-orange-500 hover:bg-orange-100 border border-orange-100'}`}
                title={isEditing ? "Save Schedule" : "Reschedule Group"}
              >
                {isEditing ? <FiCheck className="group-hover:scale-110 transition-transform" /> : <LuCalendarClock className="group-hover:scale-110 transition-transform" />}
              </button>

              <button 
                onClick={handleDelete}
                className="assess-mod-groups-action-btn bg-red-50 text-red-500 hover:bg-[#b91c1c] hover:text-white border border-red-100 group" 
                title="Delete Group"
              >
                <FiTrash2 className="group-hover:scale-110 transition-transform" />
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={handleToggleEdit}
                className={`assess-mod-groups-action-btn group ${isEditing ? 'bg-emerald-500 text-white shadow-emerald-100' : 'bg-[#f0fdf4] text-[#10b981] hover:bg-[#d1fae5]'}`}
                title={isEditing ? "Save Changes" : "Edit Group"}
              >
                {isEditing ? <FiCheck className="group-hover:scale-110 transition-transform" /> : <AssessmentEditIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />}
              </button>
              
              {!isEditing && (
                <>
                  <button
                    onClick={() => setIsPreviewOpen(true)}
                    className="assess-mod-groups-action-btn bg-indigo-50 text-indigo-500 hover:bg-indigo-500 hover:text-white group"
                    title="Preview Question Paper"
                  >
                    <FiEye className="group-hover:scale-110 transition-transform" />
                  </button>
                  <button 
                    onClick={() => {
                      setPopupConfig({
                        isOpen: true,
                        type: 'warning',
                        title: 'Publish this group?',
                        message: 'This will make the test visible to assigned candidates.',
                        confirmText: 'Yes, publish it!',
                        onConfirm: async () => {
                          if (assessment?.isLocal || String(data.id || '').startsWith('group-')) {
                            // ─── LOCAL (wizard-created) GROUP: persist to backend ───
                            // localStorage is browser-local; employees on other devices
                            // can only see tests that exist in the database.
                            try {
                              // Step 1: Create backend assessment if not yet saved
                              let backendAssessmentId = assessment?.backendId || (!assessment?.isLocal ? assessment?.id : null);
                              if (!backendAssessmentId) {
                                const rawId = String(assessment.id || '').replace(/^pub-/, '');
                                const isVideoOn = assessment.add_video === 'Yes' || assessment.add_video === true || assessment.videoEnabled === true;
                                const introVideosVal = assessment.intro_videos || assessment.videoFiles || [];

                                const createRes = await testService.createTest({
                                  name: assessment.name || 'Test',
                                  category: assessment.category || 'Internal',
                                  code: rawId || ('TST-' + Date.now()),
                                  assessment_type: assessment.type || assessment.assessment_type || '',
                                  instructions: assessment.instructions || '',
                                  description: '',
                                  pass_mark: 50,
                                  attempts: 1,
                                  proctored_exam: assessment.proctored_exam ?? (assessment.fullscreenMode ? 1 : 0),
                                  browser_lockdown: assessment.browser_lockdown ?? (assessment.tabSwitchWarning ? 1 : 0),
                                  allow_backtracking: assessment.allow_backtracking ?? (assessment.navigationControl ? 1 : 0),
                                  show_results: assessment.show_results ?? (assessment.autoSubmit ? 1 : 0),
                                  add_video: isVideoOn ? 1 : 0,
                                  intro_videos: introVideosVal
                                    .map(v => typeof v === 'string' ? v : (v && typeof v === 'object' ? (v.url || v.path || v.name) : ''))
                                    .filter(v => typeof v === 'string' && (v.startsWith('/') || v.includes('/') || v.includes('.')))
                                });
                                if (createRes.status !== 'success') {
                                  throw new Error(createRes.message || 'Failed to save assessment to database');
                                }
                                backendAssessmentId = createRes.id;
                                // Persist backendId so subsequent group publishes reuse it
                                updateLocalTestAssessment(assessmentId, { backendId: backendAssessmentId });
                              }

                              // Step 1b: Upload intro videos if present
                              const videoFilesMeta = assessment?.videoFiles || [];
                              if (videoFilesMeta.length > 0) {
                                try {
                                  const filesToUpload = [];
                                  for (const fileObj of videoFilesMeta) {
                                    const name = fileObj && typeof fileObj === 'object' ? fileObj.name : fileObj;
                                    const cachedFile = window.selectedVideoFiles?.[name];
                                    if (cachedFile) {
                                      filesToUpload.push(cachedFile);
                                    }
                                  }
                                  if (filesToUpload.length > 0) {
                                    const uploadRes = await testService.uploadIntroVideos(backendAssessmentId, filesToUpload);
                                    if (uploadRes.status !== 'success') {
                                      throw new Error(uploadRes.message || 'Failed to upload intro videos');
                                    }
                                  }
                                } catch (uploadErr) {
                                  console.error('Failed to upload intro videos during publishing:', uploadErr);
                                  throw uploadErr;
                                }
                              }

                              // Step 2: Save group as a real test pack in the database
                              const savePackRes = await testService.saveTestPack({
                                assessment_id: backendAssessmentId,
                                pack_name: data.pack_name,
                                template_id: data.template_id || assessment.template?.id || null,
                                user_role: Array.isArray(data.user_role)
                                  ? data.user_role.join(',')
                                  : (data.user_role || ''),
                                candidates: Array.isArray(data.candidates)
                                  ? data.candidates.join(',')
                                  : (data.candidates || ''),
                                scheduled_date: data.scheduled_date,
                                start_time: data.start_time,
                                end_time: data.end_time,
                                duration: data.duration || 60,
                                status: 'Draft',
                              });
                              if (savePackRes.status !== 'success') {
                                throw new Error(savePackRes.message || 'Failed to save group to database');
                              }
                              const backendPackId = savePackRes.id;

                              // Step 3: Publish the pack via backend API
                              const publishRes = await testService.publishTestPack(backendPackId);
                              if (publishRes.status !== 'success') {
                                throw new Error(publishRes.message || 'Failed to publish group');
                              }

                              // Step 4: Mirror status in localStorage for UI consistency
                              updateLocalTestGroup(assessmentId, data.id, { status: 'Published', backendPackId });
                              setPopupConfig({ isOpen: false });
                              toast.success('Group published successfully');
                              onRefresh?.();
                            } catch (err) {
                              console.error(err);
                              setPopupConfig({ isOpen: false });
                              toast.error(err?.response?.data?.message || err?.message || 'Failed to publish group');
                            }
                          } else {
                            try {
                              const res = await testService.publishTestPack(data.id);
                              if (res.status === 'success') {
                                setPopupConfig({ isOpen: false });
                                toast.success('Group published successfully');
                                onRefresh?.();
                              } else {
                                toast.error(res.message || 'Failed to publish group');
                                setPopupConfig({ isOpen: false });
                              }
                            } catch (e) {
                              toast.error('Failed to publish group');
                              setPopupConfig({ isOpen: false });
                            }
                          }
                        }
                      });
                    }}
                    className="assess-mod-groups-action-btn bg-[#fff7ed] text-[#f97316] hover:bg-[#ffedd5] group" 
                    title="Publish Group"
                  >
                    <FiSend className="group-hover:scale-110 transition-transform" />
                  </button>
                  
                  <button 
                    onClick={handleDelete}
                    className="assess-mod-groups-action-btn bg-[#fff1f2] text-[#e11d48] hover:bg-[#ffe4e6] group" 
                    title="Delete Group"
                  >
                    <FiTrash2 className="group-hover:scale-110 transition-transform" />
                  </button>
                </>
              )}
            </>
          )}
        </div>

        <QuestionPreviewModal 
          isOpen={isPreviewOpen} 
          onClose={() => setIsPreviewOpen(false)} 
          packId={data.id}
          batchName={data.pack_name}
          packSnapshot={data}
          assessment={assessment}
        />
      </td>
      <AssessmentPopup 
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
    </tr>
  );
};

export default BatchRow;
